<template>
  <div class="section-grid__container section-codifications">
    <div class="h-100 d-flex flex-column justify-content-between">
      <div class="d-flex flex-column flex-grow-1">
        <b-row class="d-flex align-items-center mb-2 mt-1 h-auto">
          <b-col>
            <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.codifications') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div>
              <b-button
                v-if="canEditCodifications"
                v-tooltip="$t('global.add')"
                v-shortkey="['ctrl', '+']"
                qa="section-codifications-add-button"
                :disabled="disabled"
                class="btn__icon btn__icon--primary px-2"
                @shortkey.prevent="addNewRow"
                @click="addNewRow"
              >
                <i
                  class="far fa-plus"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="canEditCodifications"
                v-tooltip="$t('global.delete')"
                qa="section-codifications-delete-button"
                :disabled="!selectedRows.length || disabled"
                class="btn__icon btn__icon--primary px-2"
                @click="deleteCodifications"
              >
                <i
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="canEditCodifications"
                v-tooltip="$t('diagnosis.btns.searchCod')"
                qa="section-codifications-search-button"
                class="btn__icon btn__icon--primary px-2"
                @click="showSearchCodeModal"
              >
                <i
                  class="far fa-search-plus"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row class="section-grid__height">
          <b-col>
            <vt-grid
              class="codifications-grid"
              qa="section-codifications-grid"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :autosize-columns="false"
              :row-data="rowData"
              :framework-components="frameworkComponents"
              :component-code="componentCode"
              @keydown.native="vtGridKeyDown = $event"
            />
          </b-col>
        </b-row>
      </div>
      <div class="d-flex justify-content-end">
        <vt-loading-button
          v-if="canEditCodifications"
          :disabled="disabled"
          class="btn__outline btn-sm mt-2"
          qa="section-codifications-save-button"
          :params="{ promise: save, shortkeys: ['alt', 's'] }"
        >
          <i
            class="fal fa-save mr-2"
            aria-hidden="true"
          />
          {{ $t('global.save') }}
        </vt-loading-button>
      </div>
    </div>
    <!--Modals-->
    <searchcode-modal ref="searchcodeModal" />
  </div>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import SearchcodeModal from '@/modules/diagnosisModule/views/form/modals/SearchcodeModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { commonMorphologicCodeService } from '@/api/services/common/morphologic-code/common-morphologic-code.service'
import topographicCodesServices from '@/api/resources/common/topographicCodesServices'
import { cloneDeep, isEqual, uniqBy } from 'lodash'
import VtSelectAsynchronousCellEditor from '@/cellEditors/common/VtSelectAsynchronousCellEditor'
import { AgGridColumnType, AgGridRowModelType } from '@/shared/models/ag-grid.model'

export default {
    name: 'SectionCodifications',
    components: {
        VtGrid,
        SearchcodeModal
    },
    props: {
        codificationsService: { type: Object, required: true },
        parentEntityIdsArray: { type: Array, required: true },
        disabled: { type: Boolean, default: false },
        canEditCodifications: { type: Boolean, default: true },
        outlineButton: { type: Boolean, default: true },
        componentCode: { type: String, default: 'section-codification' },
        broadcastChannelPostMessageFunction: { type: Function, default: null }
    },
    data() {
        return {
            vtGridKeyDown: null,
            originalRowData: [],
            selectedRows: [],
            frameworkComponents: {
                vtSelectAsynchronousCellEditor: VtSelectAsynchronousCellEditor
            },
            gridApi: null,
            rowData: [],
            columnDefs: [
                { type: 'customCheckbox' },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    colId: 'topographicCode.id',
                    field: 'topographicCode',
                    flex: 1,
                    headerName: this.$t('diagnosis.labels.topographic'),
                    cellEditor: 'vtSelectAsynchronousCellEditor',
                    cellRenderer: (data) => {
                        if (data.value) {
                            return this.formatTopographicCode(data.value)
                        }
                        return ''
                    },
                    onCellValueChanged: () => this.gridApi.refreshCells({ force: true }),
                    cellEditorParams: params => {
                        const value = cloneDeep(params.value)
                        if (value) {
                            value.customDescription = this.formatTopographicCode(params.value)
                        }
                        return {
                            value,
                            optionLoader: this.searchTopographicCodes,
                            label: 'customDescription',
                            ajaxSearch: true
                        }
                    },
                    editable: !this.disabled && this.canEditCodifications
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    colId: 'morphologicCode.id',
                    field: 'morphologicCode',
                    headerName: this.$t('diagnosis.labels.morphological'),
                    cellEditor: 'vtSelectAsynchronousCellEditor',
                    flex: 1,
                    cellRenderer: (data) => {
                        if (data.value) {
                            return this.formatMorphologicCode(data.value)
                        }
                        return ''
                    },
                    onCellValueChanged: () => this.gridApi.refreshCells({ force: true }),
                    cellEditorParams: params => {
                        const value = cloneDeep(params.value)
                        if (value) {
                            value.customDescription = this.formatMorphologicCode(params.value)
                        }
                        return {
                            value,
                            optionLoader: this.searchMorphologicCodes,
                            label: 'customDescription',
                            ajaxSearch: true,
                            maxHeight: 240
                        }
                    },
                    editable: !this.disabled && this.canEditCodifications
                }
            ]
        }
    },
    computed: {
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                sideBar: gridSideBarConfig,
                stopEditingWhenGridLosesFocus: true,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true // remove number in Group Column
                    }
                },
                onCellEditingStopped: params => {
                    if (this.vtGridKeyDown && this.vtGridKeyDown.keyCode === 13) {
                        params.api.setFocusedCell(params?.node.rowIndex, params.column.colId)
                    }
                }
            }
        }
    },
    methods: {
        hasBeenChanged() {
            this.deleteEmptyCodifications()
            const originalRowData = this.originalRowData.map(originalCodification => this.removeExternalOptionFromEntityObjects(originalCodification))
            const rowData = this.rowData.map(codification => this.removeExternalOptionFromEntityObjects(codification))
            return !isEqual(originalRowData, rowData)
        },
        formatTopographicCode(topographicCode) {
            if (!topographicCode) {
                return ''
            }
            return `${topographicCode.code} | ${topographicCode.description}`
        },
        formatMorphologicCode(morphologicCode) {
            if (!morphologicCode) {
                return ''
            }
            return `${morphologicCode.code} | ${morphologicCode.description}`
        },
        formatCodification(codification) {
            return this.formatTopographicCode(codification.topographicCode) + ' - ' + this.formatMorphologicCode(codification.morphologicCode)
        },
        async reloadTab() {
            await this.getRowData()
        },
        async deleteCodifications() {
            let codificationsToDelete = []
            let codificationsToDeleteWithoutId = []
            this.selectedRows.forEach((cod) => {
                if (cod.id) {
                    codificationsToDelete.push(cod)
                } else {
                    codificationsToDeleteWithoutId.push(cod)
                }
            })
            if (codificationsToDeleteWithoutId.length) {
                this.rowData = this.rowData.filter((codification) => !codificationsToDeleteWithoutId.includes(codification))
            }
            if (codificationsToDelete.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(this.codificationsService.deleteBatchCodifications(...this.parentEntityIdsArray, codificationsToDelete))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                this.showToastSuccessGlobal(this.$t('diagnosis.codifications.codificationsDeletedSucess'))
                await this.getRowData()
            }
        },
        validateCodifications() {
            const codificationsWithoutDuplicates = uniqBy(this.rowData, cod => [cod.topographicCode ? cod.topographicCode.id : null, cod.morphologicCode ? cod.morphologicCode.id : null].join())
            if (codificationsWithoutDuplicates.length !== this.rowData.length) {
                this.showErrorModalGlobal(this.$t('diagnosis.codifications.duplicatedElements'))
                return false
            }
            return true
        },
        deleteEmptyCodifications() {
            const emptyCodifications = this.rowData.filter(codification => !codification.topographicCode)
            if (emptyCodifications.length) {
                this.rowData = this.rowData.filter((codification) => !emptyCodifications.includes(codification))
            }
        },
        async save() {
            if (!this.hasBeenChanged()) {
                await this.getRowData()
                return
            }
            if (!this.validateCodifications()) {
                return
            }
            const codificationsToPost = this.rowData.filter((codification) => !codification.id).map((codification) => this.fromResponseToRequest(codification))
            const codificationsToPut = this.rowData.filter((codification) => codification.id)
                .filter((codification) => !this.originalRowData.find((originalCodification) => isEqual(originalCodification, codification)))
            for (let codification of codificationsToPut) {
                const successfulUpdate = await this.updateOneCodification(codification)
                if (!successfulUpdate) {
                    return
                }
            }
            if (codificationsToPost.length) {
                const [err] = await this.toCall(this.codificationsService.createBatchCodifications(...this.parentEntityIdsArray, codificationsToPost))
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
            }
            this.showToastSuccessGlobal()
            await this.getRowData()
            if (this.broadcastChannelPostMessageFunction) {
                this.broadcastChannelPostMessageFunction()
            }
            return true
        },
        async updateOneCodification(codification) {
            const [err] = await this.toCall(this.codificationsService.updateBatchCodifications(...this.parentEntityIdsArray, [this.fromResponseToRequest(codification)]))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    const codificationLabel = this.formatCodification(codification)
                    return this.processBasicConcurrencyConflict(this.$t('diagnosis.codifications.codification') + ` - "${codificationLabel}"`, err, () => {
                        codification.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateOneCodification(codification)
                    })
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            }
            return true
        },
        fromResponseToRequest(codification) {
            return {
                id: codification.id,
                topographicCodeId: codification.topographicCode ? codification.topographicCode.id : null,
                morphologicCodeId: codification.morphologicCode ? codification.morphologicCode.id : null,
                originalConcurrencyUpdatedAt: codification.originalConcurrencyUpdatedAt
            }
        },
        addNewRow() {
            this.rowData.push({})
        },
        async searchMorphologicCodes(query) {
            if (!query || query.length < 3) {
                return []
            }
            const results = await commonMorphologicCodeService.searchMorphologicCodes(query, { size: 20 })
            return results.map((morphologicCode) => {
                morphologicCode.customDescription = this.formatMorphologicCode(morphologicCode)
                return morphologicCode
            })
        },
        async searchTopographicCodes(query) {
            if (!query) {
                return []
            }
            const params = {
                query,
                size: 20
            }
            const results = await topographicCodesServices.search(params)
            return results.map((topographicCode) => {
                topographicCode.customDescription = this.formatTopographicCode(topographicCode)
                return topographicCode
            })
        },
        async getRowData() {
            const [err, response] = await this.toCall(this.codificationsService.getCodifications(...this.parentEntityIdsArray))
            if (err) {
                this.showSearchCodeModal()
                return
            }
            this.rowData = response
            this.originalRowData = cloneDeep(response)
            this.$emit('reloadSummary')
        },
        showSearchCodeModal() {
            this.$refs['searchcodeModal'].show()
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.getRowData()
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        }
    }
}
</script>

<style lang="scss">
    .codifications-grid .multiselect {
        margin-top: 0 !important;
    }
    .codifications-grid .multiselect__content-wrapper {
        width: 50px !important;
        max-height: 240px !important;
    }
</style>
